import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const CYCLE = [
  { key: "inhale", label: "Breathe in", duration: 4 },
  { key: "hold", label: "Hold", duration: 4 },
  { key: "exhale", label: "Breathe out", duration: 6 },
];
const CYCLE_LENGTH = CYCLE.reduce((s, p) => s + p.duration, 0); // 14s
const SESSION = 300; // 5 minutes

function phaseFromCyclePos(pos) {
  let acc = 0;
  for (const p of CYCLE) {
    if (pos < acc + p.duration) return p;
    acc += p.duration;
  }
  return CYCLE[CYCLE.length - 1];
}

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function BreathingTimer() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setElapsed((e) => {
        if (e + 1 >= SESSION) {
          setRunning(false);
          return SESSION;
        }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const complete = elapsed >= SESSION;
  const cyclePos = elapsed % CYCLE_LENGTH;
  const phase = running ? phaseFromCyclePos(cyclePos) : null;

  const scale = !running
    ? 0.62
    : phase.key === "inhale"
    ? 1
    : phase.key === "exhale"
    ? 0.62
    : 1;
  const animDuration = !running
    ? 0.6
    : phase.key === "inhale"
    ? 4
    : phase.key === "exhale"
    ? 6
    : 0.2;

  const start = () => {
    setElapsed(0);
    setRunning(true);
  };
  const stop = () => setRunning(false);

  const centerLabel = complete
    ? "Complete"
    : running
    ? phase.label
    : "Press begin";

  return (
    <div
      data-testid="breathing-timer"
      className="relative h-full border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-8 md:p-10 flex flex-col items-center overflow-hidden"
    >
      <div className="w-full flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#F4F1EC]/55 font-body">
          5-minute reset
        </span>
        <span
          data-testid="breathing-time-remaining"
          className="font-body text-[12px] tabular-nums text-[#F4F1EC]/55 tracking-widest"
        >
          {fmt(SESSION - elapsed)}
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center py-8 w-full">
        <div className="relative w-[200px] h-[200px] flex items-center justify-center">
          <motion.div
            aria-hidden
            animate={{ scale }}
            transition={{ duration: animDuration, ease: "easeInOut" }}
            className="absolute w-[200px] h-[200px] rounded-full"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, rgba(255,183,198,0.5) 0%, rgba(230,191,255,0.35) 45%, rgba(168,230,255,0.15) 75%, rgba(7,6,8,0) 100%)",
            }}
          />
          <motion.div
            aria-hidden
            animate={{ scale }}
            transition={{ duration: animDuration, ease: "easeInOut" }}
            className="absolute w-[140px] h-[140px] rounded-full border border-[#F4F1EC]/20"
          />
          <span
            data-testid="breathing-phase-label"
            className="relative font-display text-[22px] italic font-light text-[#F4F1EC] text-center px-4"
          >
            {centerLabel}
          </span>
        </div>
      </div>

      <div className="w-full flex items-center justify-center gap-3">
        {!running ? (
          <button
            data-testid="breathing-start-button"
            onClick={start}
            className="h-11 px-7 font-body text-[11px] uppercase tracking-[0.24em] bg-[#F4F1EC] text-[#070608] hover:bg-white transition-colors"
          >
            {complete ? "Begin again" : "Begin"}
          </button>
        ) : (
          <button
            data-testid="breathing-stop-button"
            onClick={stop}
            className="h-11 px-7 font-body text-[11px] uppercase tracking-[0.24em] border border-[#F4F1EC]/40 text-[#F4F1EC] hover:border-[#FFB7C6] hover:text-[#FFB7C6] transition-colors"
          >
            Pause
          </button>
        )}
      </div>
    </div>
  );
}
